import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const SELECT = "SELECT";
const PDF_ENDPOINT = "/FillMyForm/BOB_KYC1/Print.php";
const STORAGE_KEY = "smart-solutions-bob-kyc";

const titleOptions = [[SELECT, SELECT], ["MR", "MR"], ["MRS", "MRS"], ["MISS", "MISS"]];
const genderOptions = [[SELECT, SELECT], ["MALE", "MALE"], ["FEMALE", "FEMALE"]];
const maritalOptions = [[SELECT, SELECT], ["MARRIED", "MARRIED"], ["UNMARRIED", "UNMARRIED"]];
const occupationOptions = [
  [SELECT, SELECT], ["SALARIED", "SALARIED"], ["SELF_EMPLOYED", "SELF EMPLOYED"],
  ["PROFESSIONAL", "PROFESSIONAL"], ["POLITICIAN", "POLITICIAN"], ["HOUSEWIFE", "HOUSEWIFE"],
  ["STUDENT", "STUDENT"], ["DEFENCE_STAFF", "DEFENCE STAFF"], ["RETIRED", "RETIRED"],
  ["STOCK_BROKER", "STOCK BROKER"], ["AGRICULTURE", "AGRICULTURE"],
  ["ANTIQUE_DEALER", "ANTIQUE DEALER"], ["ARMS_DEALER", "ARMS DEALER"],
  ["BUSINESS", "BUSINESS"], ["OTHERS", "OTHERS"],
];

const applicantFields = [
  { name: "facid", label: "Customer ID", maxLength: 9, numeric: true },
  { name: "faacno", label: "Account Number", maxLength: 14, numeric: true },
  { name: "fatitle", label: "Title", options: titleOptions, required: true },
  { name: "faname", label: "Applicant Name", maxLength: 31, alphaOnly: true, required: true },
  { name: "fadob", label: "Date of Birth", type: "date", required: true },
  { name: "fagender", label: "Gender", options: genderOptions, required: true },
  { name: "famstatus", label: "Marital Status", options: maritalOptions, required: true },
  { name: "faaadhaarno", label: "Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true, required: true },
  { name: "fapan", label: "PAN Number", maxLength: 10, pan: true },
  { name: "fafname", label: "Father's Name", maxLength: 31, alphaOnly: true, required: true },
  { name: "famname", label: "Mother's Name", maxLength: 31, alphaOnly: true, required: true },
  { name: "fagtitle", label: "Guardian Title", options: titleOptions, guardian: true },
  { name: "fagname", label: "Guardian's Name", maxLength: 31, alphaOnly: true, guardian: true },
];

const addressFields = [
  { name: "faadd1", label: "Address Line 1", maxLength: 23, required: true },
  { name: "faadd2", label: "Address Line 2", maxLength: 23, required: true },
  { name: "facity", label: "City", maxLength: 15, required: true },
  { name: "fadist", label: "District", maxLength: 15, required: true },
  { name: "fastate", label: "State", maxLength: 36, required: true },
  { name: "fapin", label: "PIN", maxLength: 6, numeric: true, pin: true, required: true },
  { name: "famobile", label: "Mobile", maxLength: 10, numeric: true, mobile: true, required: true },
  { name: "faoccupation", label: "Occupation", options: occupationOptions, required: true },
];

const allFields = [...applicantFields, ...addressFields];
const initialFormData = Object.fromEntries(allFields.map((field) => [field.name, field.options ? SELECT : ""]));

function formatDate(value) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
}

function isMinor(dateValue) {
  if (!dateValue) return false;
  const birthDate = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(birthDate.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const beforeBirthday = today.getMonth() < birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate());
  if (beforeBirthday) age -= 1;
  return age < 18;
}

function validateField(field, value, minor) {
  const text = String(value ?? "").trim();
  const required = field.required || (field.guardian && minor);
  if (required && (!text || text === SELECT)) return `${field.label} is required.`;
  if (!text || text === SELECT) return "";
  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(text)) {
    return `${field.label} must contain alphabet letters only.`;
  }
  if (field.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(text)) {
    return "Enter a valid PAN number (e.g. ABCDE1234F).";
  }
  if (field.aadhaar && !/^\d{12}$/.test(text)) {
    return "Aadhaar number must contain exactly 12 digits.";
  }
  if (field.mobile && !/^[6-9][0-9]{9}$/.test(text)) {
    return "Enter a valid 10-digit mobile number.";
  }
  if (field.pin && !/^\d{6}$/.test(text)) {
    return "PIN must contain exactly 6 digits.";
  }
  return "";
}

export default function BOBKYC() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? { ...initialFormData, ...JSON.parse(saved) } : initialFormData;
    } catch { return initialFormData; }
  });
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);
  const minor = isMinor(formData.fadob);

  const tabs = useMemo(() => [
    { title: "Applicant", heading: "👤 Applicant Personal Details", fields: applicantFields },
    { title: "Address", heading: "🏠 Applicant Address Details", fields: addressFields },
    { title: "Preview", heading: "✅ Preview", fields: [], preview: true },
  ], []);
  const visibleFields = tabs[currentTab]?.fields || [];
  const enabledVisibleFields = visibleFields.filter((field) => !field.guardian || minor);
  const firstFieldName = enabledVisibleFields[0]?.name || "";
  const lastFieldName = enabledVisibleFields.at(-1)?.name || "";

  useEffect(() => { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData)); }, [formData]);
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);
  useEffect(() => {
    if (!firstFieldName) return undefined;
    const id = window.requestAnimationFrame(() => document.getElementById(firstFieldName)?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [currentTab, firstFieldName]);
  useEffect(() => {
    if (minor) return;
    setFormData((previous) => ({ ...previous, fagtitle: SELECT, fagname: "" }));
    setErrors((previous) => ({ ...previous, fagtitle: "", fagname: "" }));
  }, [minor]);
  useEffect(() => {
    const controller = new AbortController();

    const loadAutofill = async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent("BOBKYC")}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load autofill details.");
        }

        if (!result.found || !result.data) {
          return;
        }

        setFormData((previous) => ({
          ...previous,

          facity: previous.facity || result.data.city || "",
          fadist: previous.fadist || result.data.dist || "",
          fastate: previous.fastate || result.data.state || "",
          fapin: previous.fapin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Autofill error:", error);
        }
      }
    };

    loadAutofill();

    return () => {
      controller.abort();
    };
  }, []);

  const updateValue = (name, rawValue) => {
    const field = allFields.find((item) => item.name === name);
    let value = rawValue;

    if (field?.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field?.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    if (field?.pan) {
      value = value.toUpperCase();
    }

    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setPdfError("");
  };
  const validateFields = (fields) => {
    const nextErrors = {};
    fields.forEach((field) => {
      if (field.guardian && !minor) return;
      const message = validateField(field, formData[field.name], minor);
      if (message) nextErrors[field.name] = message;
    });
    setErrors((previous) => ({ ...previous, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };
  const handleLastFieldKeyDown = (event, fieldName) => {
    if (fieldName === lastFieldName && event.key === "Tab" && !event.shiftKey) {
      event.preventDefault(); nextButtonRef.current?.focus();
    }
  };
  const goNext = () => {
    if (!validateFields(visibleFields)) return;
    const nextTab = Math.min(currentTab + 1, tabs.length - 1);
    setCurrentTab(nextTab); setMaxAllowedTab((value) => Math.max(value, nextTab));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleSubmit = async () => {
    if (!validateFields(allFields)) { setPdfError("Please correct the highlighted fields before submitting."); return; }
    setPdfLoading(true); setPdfError("");
    try {
      const postData = new FormData();
      Object.entries(formData).forEach(([name, value]) => postData.append(name, value === SELECT ? "" : String(value ?? "")));
      const response = await fetch(PDF_ENDPOINT, { method: "POST", body: postData, credentials: "include", cache: "no-store", headers: { Accept: "application/pdf" } });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.toLowerCase().includes("application/pdf")) {
        const responseText = await response.text();
        throw new Error(responseText.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || `PDF generation failed. HTTP status: ${response.status}`);
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      setPdfUrl((oldUrl) => { if (oldUrl?.startsWith("blob:")) URL.revokeObjectURL(oldUrl); return objectUrl; });
      setShowPdf(true);
    } catch (error) { setPdfError(error.message || "Unable to generate PDF preview."); }
    finally { setPdfLoading(false); }
  };

  const renderField = (field) => {
    const disabled = field.guardian && !minor;
    const required = field.required || (field.guardian && minor);
    const error = errors[field.name];
    return <div key={field.name} className="space-y-2">
      <Label htmlFor={field.name}>{field.label}{required && <span className="text-red-500"> *</span>}</Label>
      {field.options ? <Select value={formData[field.name]} disabled={disabled} onValueChange={(value) => updateValue(field.name, value)}>
        <SelectTrigger id={field.name} className={error ? "border-red-500" : ""} onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}>
          <SelectValue placeholder={`Select ${field.label}`} />
        </SelectTrigger>
        <SelectContent>{field.options.map(([value, label]) => <SelectItem key={`${field.name}-${value}-${label}`} value={value}>{label}</SelectItem>)}</SelectContent>
      </Select> : <Input id={field.name} name={field.name} type={field.type || "text"} value={formData[field.name]}
        placeholder={field.label} maxLength={field.maxLength} inputMode={field.numeric ? "numeric" : undefined} disabled={disabled}
        onChange={(event) => updateValue(field.name, event.target.value)} onKeyDown={(event) => handleLastFieldKeyDown(event, field.name)}
        className={error ? "border-red-500" : ""} autoComplete="on" />}
      {field.guardian && !minor && <p className="text-sm text-muted-foreground">Enabled automatically when the applicant is under 18.</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>;
  };

  if (showPdf && pdfUrl) return <PdfViewerContainer pdfUrl={pdfUrl} title="BOB KYC Form" backRoute="/Fill_My_Form" onBack={() => setShowPdf(false)} />;

  const previewGroups = [
    { title: "Applicant Personal Details", fields: applicantFields },
    { title: "Applicant Address Details", fields: addressFields },
  ].map((group) => ({ title: group.title, rows: group.fields.map((field) => [field.label,
    field.name === "fadob" ? formatDate(formData[field.name]) : field.options?.find(([value]) => value === formData[field.name])?.[1] || formData[field.name],
  ]) }));

  return <div className="min-h-screen bg-background p-6"><div className="max-w-7xl mx-auto">
    <Card className="mb-6"><CardHeader><CardTitle className="text-3xl">🏦 Bank of Baroda KYC Form</CardTitle>
      <p className="text-muted-foreground">Fill customer KYC details carefully.</p></CardHeader></Card>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">{tabs.map((tab, index) =>
      <Button key={tab.title} type="button" variant={currentTab === index ? "default" : "outline"} disabled={index > maxAllowedTab || pdfLoading} onClick={() => setCurrentTab(index)}>{index + 1}. {tab.title}</Button>)}</div>
    {currentTab < tabs.length - 1 && <Card><CardHeader><CardTitle>{tabs[currentTab].heading}</CardTitle></CardHeader><CardContent>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{visibleFields.map(renderField)}</div>
    </CardContent></Card>}
    {currentTab === tabs.length - 1 && <Card><CardHeader><CardTitle>✅ Preview</CardTitle></CardHeader><CardContent className="space-y-8">
      {previewGroups.map((group) => <PreviewSection key={group.title} title={group.title} rows={group.rows} />)}
    </CardContent></Card>}
    {pdfLoading && <div className="mt-6 text-center">Generating PDF, please wait...</div>}
    {pdfError && <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">{pdfError}</div>}
    <div className="flex justify-between mt-8">
      <Button type="button" variant="outline" onClick={() => setCurrentTab((tab) => Math.max(0, tab - 1))} disabled={currentTab === 0 || pdfLoading}>← Previous</Button>
      {currentTab === tabs.length - 1 ? <Button type="button" onClick={handleSubmit} disabled={pdfLoading}>{pdfLoading ? "Generating PDF..." : "Submit"}</Button>
        : <Button ref={nextButtonRef} type="button" onClick={goNext} disabled={pdfLoading}>{currentTab === tabs.length - 2 ? "Preview" : "Next →"}</Button>}
    </div>
  </div></div>;
}

function PreviewSection({ title, rows }) {
  return <div className="rounded-2xl border p-6"><h3 className="text-xl font-semibold text-center mb-6">{title}</h3>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{rows.map(([label, value]) =>
      <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2"><div className="font-medium text-muted-foreground">{label}</div><div>{value && value !== SELECT ? value : "-"}</div></div>)}</div>
  </div>;
}
